import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService, CommonApiResponse, LoginResponseAdapter } from '@techextensor/tab-core-utility';
import { TabSdk } from '@techextensor/tab-sdk';
import { MicrosoftSsoApi } from '../const/constants';
import { environment } from '../../../environments/environment';

/** One Microsoft organisation connected to the current tenant. */
export interface MicrosoftOrganisation {
  directoryId: string;
  /** Email domain of the admin who connected it, e.g. `contoso.com`. */
  displayName: string;
  connectedBy?: string | null;
  connectedAtUtc: string;
}

export interface MicrosoftSignInSettings {
  /** False while the TAF server has no Microsoft app registration configured. */
  isAvailable: boolean;
  /** The link a Microsoft IT admin opens to approve TAF for their organisation. */
  adminConsentUrl?: string | null;
  organisations: MicrosoftOrganisation[];
}

/**
 * Connects the current tenant to a Microsoft organisation through the Identity service.
 *
 * Every call goes through core-utility's `ApiService`, so the auth interceptor attaches the token and
 * TenantId, and the response comes back normalised (`IsSuccess` / `Result` / `Errors`). The tenant
 * administrator check happens on the server; this service does not decide who may call it.
 */
@Injectable({ providedIn: 'root' })
export class MicrosoftSsoService {
  private readonly _api = inject(ApiService);

  async getSettings(): Promise<MicrosoftSignInSettings> {
    const response = await firstValueFrom(this._api.getFromService('identityService', MicrosoftSsoApi.settings));
    const result = this.unwrap(response);
    return {
      isAvailable: !!result?.isAvailable,
      adminConsentUrl: result?.adminConsentUrl ?? null,
      organisations: result?.organisations ?? []
    };
  }

  /**
   * Asks the server for the Microsoft sign-in URL. The caller sends the whole page there; the server
   * brings the browser back to `returnUrl` with the outcome in the query string.
   */
  async startConnect(returnUrl: string): Promise<string> {
    const response = await firstValueFrom(
      this._api.postToService('identityService', MicrosoftSsoApi.connect, { returnUrl })
    );
    const url = this.unwrap(response)?.authorizationUrl;
    if (!url) throw new Error('No Microsoft sign-in address was returned.');
    return url;
  }

  async disconnect(directoryId: string): Promise<void> {
    const response = await firstValueFrom(
      this._api.deleteFromService('identityService', MicrosoftSsoApi.organisation(directoryId), null)
    );
    this.unwrap(response);
  }

  /** Whether the sign-in page should show "Sign in with Microsoft" for this tenant. Never throws. */
  async isSignInAvailable(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this._api.getFromService('identityService', MicrosoftSsoApi.signInAvailability)
      );
      return response?.IsSuccess === true && response.Result === true;
    } catch {
      return false;
    }
  }

  /**
   * The address that starts a Microsoft sign-in. A plain navigation, not an API call: the browser goes to
   * the Identity service, then Microsoft, then back to `returnUrl`. The tenant/app are hints so the person
   * lands where this page belongs; the server decides what they may actually enter.
   */
  signInStartUrl(returnUrl: string): string {
    const params = new URLSearchParams({ returnUrl });
    const tenantId = TabSdk.store.tenantId ?? environment.commonConfig.tenantId;
    const appId = TabSdk.store.appId ?? environment.commonConfig.applicationId;
    const environmentId = TabSdk.store.environmentId ?? environment.id;
    if (tenantId) params.set('tenantId', tenantId);
    if (appId) params.set('appId', appId);
    if (environmentId) params.set('environmentId', environmentId);
    return `${environment.apiServiceUrls.identityService}${MicrosoftSsoApi.signInStart}?${params}`;
  }

  /**
   * Trades the one-time code for the session. The body is the same as the password login's, so it goes through
   * the same LoginResponseAdapter the password login uses.
   */
  async exchangeSignInCode(code: string): Promise<CommonApiResponse> {
    const response = await firstValueFrom(
      this._api.postToService('identityService', MicrosoftSsoApi.signInExchange, { code })
    );
    const normalized = LoginResponseAdapter.normalize(response);
    this.unwrap(normalized);
    return normalized;
  }

  private unwrap(response: CommonApiResponse): any {
    if (!response?.IsSuccess) {
      throw new Error(response?.Errors?.[0] ?? 'The request was not completed.');
    }
    return response.Result;
  }
}
